import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppQuoteContext } from "../../../../../../context/AppQuoteContext";
import { columns } from "../ActionButtons";
import { getAllColumns, getHeaders, getItems, getRows, formatDateFormat } from "../Helpers";
import { useSelectedRowContext } from "../TabsWrapper/SelectedRowContext";
import dayjs from "dayjs";
import useDarkMode from "../WidgetToolbar/widgetToolbarHelpers";
import { useEditorContext } from "../context/editorContext";
import "../styles/grid.css";
import useDataGridBoilerplateHelpers, {
	shouldRoutePackagePriceToSetPackagePrice
} from "./dataGridBoilerplateHelpers";

//Batches quote.forceUpdate after grid field saves so rapid edits trigger one refresh
const FORCE_UPDATE_AFTER_FIELD_SAVE_MS = 500;

const useDataGridBoilerplateContainer = (props) => {
	const {
		tabItem,
		expanded,
		openCustomizeGrid,
		setLoading,
		setChange,
		onRemove,
		marginModalRef,
		newRecordRef,
		priceSourceModalRef
	} = props;
	const { handleEdit } =
		useDataGridBoilerplateHelpers(props);
	const { quote, updateQuoteItems } = useAppQuoteContext();
	const { selectedRows, setSelectedRows, filteredRows } = useSelectedRowContext();
	const lastCheckboxAnchorRef = useRef(null);
	const [ openDialog, setOpenDialog ] = useState(false);
	const [ gridData, setGridData ] = useState([]);
	const [ rowToDelete, setRowToDelete ] = useState(null);
	const items = useMemo(()=> quosal.util.getItemsByTabId(quote, tabItem?.IdQuoteTabs), [quote, tabItem?.IdQuoteTabs]);
	const gridRows = useMemo( ()=> getRows(tabItem?.IdQuoteTabs, quote), [ items[tabItem?.IdQuoteTabs]]);
	let orderedRows = useMemo( ()=> getRows(tabItem?.IdQuoteTabs, quote),  [ items[tabItem?.IdQuoteTabs]]);

	const [ openMargins, setOpenMargins ] = useState(false);
	const [ editableCell, setEditableCell ] = useState(null);
	const [ editFieldPrevValue, setEditFieldPrevValue ] = useState(null);
	const [ modalData, setModalData ] = useState({});
	const [ anchorEl, setAnchorEl ] = useState(null);
	const [ updateApiRequests, setUpdateApiRequests ] = useState([]);
	const [ defaultDropdownValue, setDefaultDropdownValue ] = useState("");
	const forceUpdateDebounceRef = useRef(null);
	const { manualSyncEnabled, partialItemUpdate } = useAppQuoteContext();

	useEffect(() => {
		const idQuoteMain = quote?.IdQuoteMain;
		return () => {
			if (forceUpdateDebounceRef.current !== null) {
				clearTimeout(forceUpdateDebounceRef.current);
				forceUpdateDebounceRef.current = null;
				if (idQuoteMain != null) {
					quosal.sell.quote.clearPendingDebouncedForceUpdate(idQuoteMain);
				}
			}
		};
	}, [quote?.IdQuoteMain]);

	const darkMode = useDarkMode();

	const { isDisabledQuoteActions, contentGrid, rowActions, buildNotesDialog } = useEditorContext();

	const isStandardUser =
		!app.currentUser.IsContentMaintainer &&
		!app.currentUser.IsAdministrator &&
		!app.currentUser.IsStandardPlus;
	const isStandardUserPlus =
		!app.currentUser.IsContentMaintainer &&
		!app.currentUser.IsAdministrator &&
		app.currentUser.IsStandardPlus;

	const isStandardUserAndProtectedTab = isStandardUser && tabItem?.IsProtectedTab;
	const isStandardUserAndDisableNewProductCreation =
		isStandardUser && tabItem?.HasLimitOnNewItems;

	const disableNewProductCreationStyle =
		isStandardUserAndDisableNewProductCreation ||
			isStandardUserAndProtectedTab ||
			isDisabledQuoteActions
			? { pointerEvents: "none", opacity: 0.38 }
			: {};

	const isLockStandardUsersOutOfItemEdit = quosal.settings.getValue(
		"LockStandardUsersOutOfItemEdit"
	);
	const isStandardUserAndLockItemEdit = isStandardUser && isLockStandardUsersOutOfItemEdit;
	const canModifyProtectedItem = quosal.util.userCanModifyProtectedItem();

	const handleModalClose = () => {
		setAnchorEl(null);
		setModalData({});
	};

	const open = Boolean(anchorEl);
	const id = open ? "price-modifier-popover" : undefined;

	const priceSourceLoaded = quosal.sell.product.sourceGroups != null;

	const handlePriceSourceModalClick = (e, params) => {
		if (e) {
			const dataModal = {
				id: "price-source-popover",
				anchorEl: e.currentTarget,
				params,
				grid: contentGrid,
				type: "cloudsourcing",
			};
			priceSourceModalRef.current.showModal(true, dataModal);
		}
	};

	const handleInsertSectionGrouping = useCallback((sectionType) => {
		const tab = getCurrentTab();
		const tabItems = getItems(tab.IdQuoteTabs);
		let internalSelectedRowIndexes = selectedRows?.map((itemId) => tabItems.find((tabItem)=> tabItem.IdQuoteItems === itemId)?.SortOrder || 0) || [];
		let newItems = [];

		if (!internalSelectedRowIndexes.length) {
			if (sectionType === 'SubHeader') {
				internalSelectedRowIndexes.push(0);
			} else if (sectionType === 'SubFooter') {
				internalSelectedRowIndexes.push(tabItems.length);
			} else if (sectionType === 'Comment') {
				internalSelectedRowIndexes.push(tabItems.length);
			}
		}

		for (const rowIndex of internalSelectedRowIndexes) {
			let relativeIndex = rowIndex;
			if (sectionType ===  'SubFooter' || sectionType === 'Comment') {
				relativeIndex += 1;
			}

			let newItem = {
				table: 'QuoteItems',
				IdQuoteMain: quote.IdQuoteMain,
				IdQuoteTabs: tab.IdQuoteTabs,
				id: quosal.util.generateGuid(),
				LineType: sectionType,
				IsTotalsIncluded: false,
				SortOrder: relativeIndex
			}

			if (sectionType === 'SubHeader') {
				newItem.LongDescription = 'New Section';
			} else if (sectionType === 'SubFooter') {
				newItem.LongDescription = 'Section Subtotal';
			}
			newItems.push(newItem);
		}

		var insertSectionGroupingApi = quosal.api.product.insertSectionGrouping(
			quote.IdQuoteMain, tab.IdQuoteTabs, newItems.map(i=>i.SortOrder),  newItems.map(i=>i.id), sectionType
		);

		insertSectionGroupingApi.finished = function (msg) {
			for (const newItem of newItems) {
				const savedItem = msg.partialResponse.items[newItem.id];
				const mergedItem = {...newItem, ...savedItem};
				
				var quoteInsertIndex = quote.Items?.findIndex(s => s.IdQuoteTabs === tab.IdQuoteTabs && s.SortOrder === mergedItem.SortOrder) || 0;
				
				tabItems.splice(mergedItem.SortOrder, 0, mergedItem);
				quote.Items.splice(quoteInsertIndex, 0, mergedItem);				
			}
			quosal.sell.quote.updateFromApiResponse(msg);
			setSelectedRows([]);
			setLoading(false);
		}
		setLoading(true);
		insertSectionGroupingApi.call();
	})

	const handleSelectAll = useCallback(
		(checked) => {
			if (checked) {
				const allVisibleRowIds = gridRows
					.filter(row => row && row.idQuoteItems && !filteredRows.includes(row.idQuoteItems))
					.map(row => row.idQuoteItems);

				setSelectedRows(allVisibleRowIds);
			} else {
				lastCheckboxAnchorRef.current = null;
				setSelectedRows([]);
			}
		},
		[ gridRows, filteredRows ]
	);

	useEffect(() => {
		lastCheckboxAnchorRef.current = null;
	}, [tabItem?.IdQuoteTabs]);

	const getVisibleRowIdsInOrder = useCallback(() => {
		return gridRows
			.filter((row) => row && row.idQuoteItems && !filteredRows.includes(row.idQuoteItems))
			.map((row) => row.idQuoteItems);
	}, [gridRows, filteredRows]);

	const handleRowSelection = useCallback(
		(rowIdOrIds, checkedOrRemoveFlag, event) => {
			if (Array.isArray(rowIdOrIds) && checkedOrRemoveFlag === false && event === undefined) {
				const toRemove = new Set(rowIdOrIds);
				setSelectedRows((prev) => prev.filter((idRow) => !toRemove.has(idRow)));
				return;
			}

			const rowId = rowIdOrIds;
			const checked = checkedOrRemoveFlag;
			const shiftKey = !!(event && event.nativeEvent && event.nativeEvent.shiftKey);
			const visibleIds = getVisibleRowIdsInOrder();

			if (shiftKey && lastCheckboxAnchorRef.current != null) {
				const anchorId = lastCheckboxAnchorRef.current;
				const iAnchor = visibleIds.indexOf(anchorId);
				const iRow = visibleIds.indexOf(rowId);
				if (iAnchor !== -1 && iRow !== -1) {
					const low = Math.min(iAnchor, iRow);
					const high = Math.max(iAnchor, iRow);
					const rangeIds = visibleIds.slice(low, high + 1);
					// Legacy prepare content: shift+click range always selects—never deselects.
					setSelectedRows((prev) => {
						const next = new Set(prev);
						rangeIds.forEach((id) => next.add(id));
						return Array.from(next);
					});
					lastCheckboxAnchorRef.current = rowId;
					return;
				}
			}

			// Legacy: while Shift is held, checkbox clicks cannot remove rows from the selection.
			if (shiftKey && !checked) {
				return;
			}

			setSelectedRows((prev) => {
				if (checked) {
					lastCheckboxAnchorRef.current = rowId;
					if (prev.includes(rowId)) {
						return prev;
					}
					return [...prev, rowId];
				}
				if (lastCheckboxAnchorRef.current === rowId) {
					lastCheckboxAnchorRef.current = null;
				}
				return prev.filter((idRow) => idRow !== rowId);
			});
		},
		[getVisibleRowIdsInOrder]
	);

	const handleDeleteConfirmed = useCallback(
		(rowsToDelete) => {
			handleRowSelection(
				rowsToDelete.map((row) => row.idQuoteItems),
				false
			);
			setLoading(true);
			const deleteApi = quosal.api.data.delete(
				{
					table: "QuoteItems",
					where: [
						{
							field: "IdQuoteItems",
							operator: "In",
							value: rowsToDelete.map((row) => row.idQuoteItems)
						}
					]
				},
				quote.IdQuoteMain
			);
			deleteApi.finished = function (msg) {
				quosal.sell.quote.updateFromApiResponse(msg);
				setLoading(false);
				setSelectedRows([]);
			};
			deleteApi.call();
			onRemove(false);
		},
		[
			gridRows,
			handleRowSelection,
			onRemove,
			orderedRows,
			setLoading,
			setSelectedRows,
			tabItem?.IdQuoteTabs
		]
	);

	const isDisabledGridActions = () => !selectedRows.length;

	const handleDelete = (row) => {
		let rowsToDeleteWithPackageItems = [row];
		let totalBundle = 0;
		const cannotModifyProtectedItem = !canModifyProtectedItem && row.IsProtectedItem;
		const hasProtectedItem = false;
		
		if (cannotModifyProtectedItem) {
			hasProtectedItem = true;
			rowsToDeleteWithPackageItems = [];
		}		

		if (row.isPackageHeader && !cannotModifyProtectedItem) {
			totalBundle++;
			const packageItems = gridRows.filter((r) => r.ParentQuoteItem === row.idQuoteItems);
			rowsToDeleteWithPackageItems = [...new Set([...rowsToDeleteWithPackageItems, ...packageItems])];
		}
		onRemove(true, rowsToDeleteWithPackageItems, totalBundle > 0, totalBundle, hasProtectedItem, handleDeleteConfirmed);
	};

	const handleClose = () => {
		setOpenDialog(false);
		setRowToDelete([]);
	};

	const updateFieldCallback = useCallback(
		(el) => {
			el.callback.call(contentGrid, gridData);
		},
		[contentGrid, gridData]
	);
	
	const getCurrentTab = useCallback(() => {
		let currentTab = [];
		quote.Tabs.map((tab) => {
			if (tab.IdQuoteTabs === tabItem?.IdQuoteTabs) {
				currentTab = tab;
			}
		});
		return currentTab;
	}, [tabItem?.IdQuoteTabs]);

	const calculateMargins = (isShown) => {
		const currentTab = getCurrentTab();
		const selectedGridRows = selectedRows;

		const idQuoteMain = currentTab.IdQuoteMain;
		const productIds = [];
		for (let i = 0; i < selectedGridRows.length; i++) {
			productIds.push(selectedGridRows[i]);
		}

		const calculateMarginsApi = quosal.api.product.calculateMargins(idQuoteMain, productIds);
		calculateMarginsApi.finished = function (msg) {
			const marginsProperties = {
				price: msg.price,
				cost: msg.cost,
				profitAmount: msg.profitAmount,
				marginPercent: msg.marginPercent,
				markupPercent: msg.markupPercent,
				suggestedPrice: msg.suggestedPrice,
				discountPercent: msg.discountPercent
			};

			const marginModalData = {
				titleText: "Product Margins",
				props: marginsProperties
			};

			marginModalRef.current.showModal(isShown, marginModalData);
		};

		calculateMarginsApi.call();
	};

	const colParams = {
		gridRows,
		handleDelete,
		handleEdit,
		handleRowSelection,
		handleSelectAll,
		selectedRows,
		handlePriceSourceModalClick,
		priceSourceLoaded,
		grid: contentGrid
	};

	const saveGroupMasterQuantity = useCallback(
		(newRow) => {
			const currentTab = getCurrentTab();
			let currentProduct = null;
			quote.Items.map((item) => {
				if (item.IdQuoteItems === newRow.idQuoteItems) {
					currentProduct = item;
				}
			});
			if (currentTab.TabGroup && currentTab.IsGroupQuantityMaster) {
				quote.Tabs.map((tab) => {
					if (tab.TabGroup === currentTab.TabGroup &&
						tab.IdQuoteTabs !== currentTab.IdQuoteTabs
					) {
						const tabItems = getItems(tab.IdQuoteTabs);
						tabItems.map((item) => {
							if (item.GroupingCode === currentProduct.GroupingCode ||
								item.ManufacturerPartNumber ===
									currentProduct.ManufacturerPartNumber
							) {
								item.Quantity = newRow.Quantity;
								quote.Items.map((product) => {
									if (product.IdQuoteItems === item.IdQuoteItems) {
										product.Quantity = item.Quantity;
									}
								});
							}
						});
					}
				});
			}
		},
		[getCurrentTab]
	);

	const saveEditedField = useCallback(
		(newRow, editedCell) => {
			if (newRow[editedCell] !== editFieldPrevValue) {
				if (shouldRoutePackagePriceToSetPackagePrice(newRow, editedCell)) {
					const headerId = newRow.idQuoteItems || newRow.IdQuoteItems;
					const amount = newRow[editedCell] || 0;
					const isRecurring = editedCell === "RecurringAmount";
					const setPackagePriceApi = quosal.api.product.setPackagePrice(
						quote.IdQuoteMain,
						headerId,
						amount,
						isRecurring
					);
					setPackagePriceApi.finished = function (msg) {
						if (msg.quote) {
							quosal.sell.quote.update(msg.quote);
						}
					};
					setPackagePriceApi.call();
					return;
				}

				const fieldChangeUpdates = [];
				const apiRequests = updateApiRequests;
				fieldChangeUpdates.push({
					fields: { [editedCell]: newRow[editedCell] },
					queries: [
						{
							table: "QuoteItems",
							where: [
								{
									field: "IdQuoteItems",
									operator: "Equals",
									value: newRow.idQuoteItems
								}
							]
						}
					]
				});
				
				if (manualSyncEnabled) {
					partialItemUpdate(newRow, fieldChangeUpdates);
					return;
				}

				saveGroupMasterQuantity(newRow);
				if (forceUpdateDebounceRef.current !== null) {
					clearTimeout(forceUpdateDebounceRef.current);
					forceUpdateDebounceRef.current = null;
				}
				const updateApi = quosal.api.data.update(
					fieldChangeUpdates,
					quote.IdQuoteMain,
					true
				);
				apiRequests.push(updateApi.opId);
				setUpdateApiRequests(apiRequests);

				updateApi.setFlag("fieldChanged");

				updateApi.finished = function (msg) {
					apiRequests.splice(apiRequests.indexOf(msg.opId), 1);
					setUpdateApiRequests(apiRequests);
					const updateQuote = apiRequests.length === 0;
					if (updateQuote) {
						if (forceUpdateDebounceRef.current !== null) {
							clearTimeout(forceUpdateDebounceRef.current);
						}
						const idQuoteMain = quote.IdQuoteMain;
						quosal.sell.quote.markPendingDebouncedForceUpdate(idQuoteMain);
						forceUpdateDebounceRef.current = setTimeout(() => {
							forceUpdateDebounceRef.current = null;
							const forceUpdateApi = quosal.api.quote.forceUpdate(idQuoteMain, true);
							forceUpdateApi.finished = function (msg2) {
								quosal.sell.quote.updateFromApiResponse(msg2);
							};
							forceUpdateApi.call();
						}, FORCE_UPDATE_AFTER_FIELD_SAVE_MS);
					}

					const applyDataUpdatePartial = () => {
						if (msg.partialResponse) {
							quosal.sell.quote.updatePartial(msg.partialResponse);
							if (msg.partialResponse.partialQuote &&
								msg.partialResponse.partialQuote.ShouldGeneratePdfAfterSave
							) {
								quosal.sell.product.generatePdf(quote.IdQuoteMain, msg.opId);
							}
						}
					};

					// Partial payload does not include recalculated unit/extended price; merge would leave stale
					// prices until forceUpdate. Match server pricing using the same APIs as PriceModifierHelper.
					if (
						msg.partialResponse &&
						(editedCell === "PriceModifier" || editedCell === "RecurringPriceModifier")
					) {
						const pmText = newRow[editedCell];
						const isRecurring = editedCell === "RecurringPriceModifier";
						const parseApi = quosal.api.priceModifierHelper.ParsePriceModifierText(pmText);
						parseApi.finished = function (parseMsg) {
							let paramObj = null;
							try {
								let rawParams =
									parseMsg.data && parseMsg.data.modifierParams != null
										? parseMsg.data.modifierParams
										: null;
								if (Array.isArray(rawParams)) {
									rawParams = rawParams[0];
								}
								if (rawParams == null) {
									applyDataUpdatePartial();
									return;
								}
								paramObj =
									typeof rawParams === "string" ? JSON.parse(rawParams) : rawParams;
							} catch (e) {
								quosal.log.error(e);
								applyDataUpdatePartial();
								return;
							}
							const itemCost = isRecurring
								? +newRow.RecurringCost || 0
								: +newRow.Cost || 0;
							const suggested = isRecurring
								? +newRow.RecurringSuggestedPrice || 0
								: +newRow.SuggestedPrice || 0;
							const base = isRecurring
								? +newRow.RecurringBasePrice || 0
								: +newRow.BasePrice || 0;
							const calcApi = quosal.api.priceModifierHelper.CalculateSellingPrice(
								itemCost,
								suggested,
								base,
								!!quote.EnableAdvancedRounding,
								paramObj
							);
							calcApi.finished = function (calcMsg) {
								const lineId = newRow.idQuoteItems || newRow.IdQuoteItems;
								if (
									lineId &&
									app.currentQuote &&
									app.currentQuote.Items &&
									calcMsg &&
									!calcMsg.validationErrors &&
									typeof calcMsg.sellingPrice === "number" &&
									!isNaN(calcMsg.sellingPrice)
								) {
									const line = app.currentQuote.Items.find(
										(item) => item.IdQuoteItems == lineId
									);
									if (line) {
										const qty = +line.Quantity || +newRow.Quantity || 0;
										const sp = calcMsg.sellingPrice;
										if (isRecurring) {
											line.RecurringPriceModifier = pmText;
											line.RecurringPrice = sp;
											line.RecurringAmount = sp;
											line.RecurringExtendedPrice = quote.parseCurrency(sp * qty);
										} else {
											line.PriceModifier = pmText;
											line.QuoteItemPrice = sp;
											line.ExtendedPrice = quote.parseCurrency(sp * qty);
										}
									}
								}
								applyDataUpdatePartial();
							};
							calcApi.call();
						};
						parseApi.call();
					} else {
						applyDataUpdatePartial();
					}

					if (fieldChangeUpdates.length === 0 && msg.quote) {
						const unsaved = props.quote.Items.filter((s) => s.isUnsaved);
						for (let i = 0; i < unsaved.length; i++) {
							msg.quote.Items.push(unsaved[i]);
						}
						quosal.sell.quote.update(msg.quote);
					}
				};

				updateApi.call();
			}
		},
		[
			editFieldPrevValue,
			props?.quote?.Items,
			quote,
			saveGroupMasterQuantity,
			tabItem?.IdQuoteTabs,
			updateApiRequests
		]
	);

	const actionButtons = columns({
		...colParams,
		isStandardUserAndProtectedTab,
		isStandardUserAndLockItemEdit,
		canModifyProtectedItem
	});

	const handleDateChange = useCallback(
		(cellValues, date) => {
			let parsedDate = "";
			if (date) {
				parsedDate = dayjs(date.$d).format(formatDateFormat(app?.settings?.user?.dateFormat || "MM/DD/YYYY"));
			}
			
			cellValues.row[cellValues.field] = parsedDate;
			saveEditedField(cellValues.row, cellValues.field);
		},
		[saveEditedField]
	);

	const handleOptionChange = useCallback(
		(cellValues, event) => {
			const { value } = event.target;
			cellValues.row[cellValues.field] = value;
			saveEditedField(cellValues.row, cellValues.field);
		},
		[saveEditedField]
	);

	const handleModalClick = (e) => {
		if (e) {
			setAnchorEl(e.currentTarget);
		}
	};

	const gridColumns = getAllColumns(
		actionButtons,
		tabItem?.IdQuoteTabs,
		buildNotesDialog,
		handleDateChange,
		handleOptionChange,
		setChange,
		isStandardUserAndProtectedTab,
		canModifyProtectedItem,
		app.currentUser.IsAdministrator,
		handleModalClick,
		setModalData,
		contentGrid
	).map((item) => (item?.field === "selection" ? { ...item, width: 40 } : item));

	const validateEditedValue = useCallback(
		(newRow) => {
			const headers = getHeaders(tabItem?.IdQuoteTabs);
			const dataType = headers.filter((header) => header.FieldName === editableCell)[0].DataType;
			let errorMsg = null;

			switch (editableCell) {
				case "PriceModifier":
					let priceModifierValue = newRow[editableCell];
					const isPriceModifierValue = priceModifierValue.match(quosal.fullModifierRegex);
					if (!isPriceModifierValue) {
						errorMsg = "Not valid string";
					}
					break;
				case "ExtendedCost":
				case "ExtendedPrice":
				case "ExtendedSuggestedPrice":
				case "RecurringExtendedCost":
				case "RecurringExtendedPrice":
				case "RecurringExtendedSuggestedPrice":
					errorMsg = "Should not edit";
					break;
				case "QuoteItemPrice":
				case "SuggestedPrice":
				case "RecurringPrice":
				case "RecurringSuggestedPrice":
				case "RecurringAmount":
					if ((editableCell == "QuoteItemPrice" || editableCell == "RecurringAmount") && newRow[editableCell] === "") {
						break;
					}
					const newValue = quote.parseCurrency(newRow[editableCell]);
					if (newValue > quosal.data.MAX_INTEGER) {
						errorMsg = quosal.data.MAX_INTEGER;
					}
					break;
				case "CostModifier":
				case "RecurringCostModifier":
					let value = newRow[editableCell];
					const validString = value.match(quosal.costModifierRegex);

					if (!validString) {
						errorMsg = "Not valid string";
					}
					break;
				case "ImportedItemDiscountStartDate":
				case "PromotionExpirationDate":
				case "ImportedQuoteDiscountStartDate":
				case "ImportedQuoteDiscountExpirationDate":
				case "RebateExpirationDate":
				case "ModifyDate":
				case "ExternalQuoteExpiration":
					value = newRow[editableCell];
					if (!dayjs(value).isValid()) {
						errorMsg = "Not a date";
					}
					break;
				default:
					errorMsg = null;
					break;
			}

			if (dataType === "Double") {
				if ((editableCell === "QuoteItemPrice" || editableCell === "RecurringAmount") && newRow[editableCell] === "") {
					return errorMsg;
				}
				
				const numberToCheck = typeof newRow[editableCell] === 'number'
					? newRow[editableCell]
					: quote.parseCurrency(newRow[editableCell]);
					
				if (Number.isNaN(numberToCheck)) {
					errorMsg = "Not a number";
				} else {
					newRow[editableCell] = numberToCheck;
				}
			}

			return errorMsg;
		},
		[editableCell, tabItem?.IdQuoteTabs]
	);

	const shouldReturnPreviousValue = useCallback(
		() => editableCell !== "PriceModifier" && editableCell !== "CostModifier" && editableCell !== "RecurringCostModifier" && editableCell !== "QuoteItemPrice",
		[editableCell]
	);

	const processRowUpdate = useCallback(
		(newRow) => {
			if (editableCell !== null) {
				if (validateEditedValue(newRow) === null) {
					saveEditedField(newRow, editableCell);

					if (editableCell === "QuoteItemPrice" || editableCell === "Quantity") {
						newRow.ExtendedPrice =
							editableCell === "QuoteItemPrice"
								? quote.parseCurrency(
									newRow.Quantity * +newRow[editableCell]
								)
								: quote.parseCurrency(
									newRow.QuoteItemPrice * +newRow[editableCell]
								);
						// data.update partial merge only sends edited columns (e.g. Quantity), not ExtendedPrice.
						// Without updating the canonical line item, merge leaves a stale ExtendedPrice until forceUpdate.
						const lineId = newRow.idQuoteItems || newRow.IdQuoteItems;
						if (lineId && app.currentQuote && app.currentQuote.Items) {
							const line = app.currentQuote.Items.find(
								(item) => item.IdQuoteItems == lineId
							);
							if (line) {
								line[editableCell] = newRow[editableCell];
								line.ExtendedPrice = newRow.ExtendedPrice;
							}
						}
					} else if (editableCell === "PriceModifier" || editableCell === "RecurringPriceModifier") {
						const lineId = newRow.idQuoteItems || newRow.IdQuoteItems;
						if (lineId && app.currentQuote && app.currentQuote.Items) {
							const line = app.currentQuote.Items.find(
								(item) => item.IdQuoteItems == lineId
							);
							if (line) {
								line[editableCell] = newRow[editableCell];
							}
						}
					}
				} else if (shouldReturnPreviousValue()) {
					newRow[editableCell] = editFieldPrevValue;
				}
			}
			return newRow;
		},
		[
			editFieldPrevValue,
			editableCell,
			saveEditedField,
			shouldReturnPreviousValue,
			validateEditedValue
		]
	);

	const getFixRowOrderUpdates = useCallback(
		(params) => {
			let offset = params.oldIndex - params.targetIndex > 0 ? -.05 : .05;
			let dropIndex = params.targetIndex;
			
			if (orderedRows[params.targetIndex].IsPackageItem ) {
				const parentRow = orderedRows[params.targetIndex].parentRow;
				const bundleSize = parentRow.childRows.length;
				//Move dropped non bundle item to the top of the package if its moving upwards
				if (offset < 0) {
					dropIndex = parentRow.SortOrder;
				} else {
					//Move dropped non bundle item to the bottom of the package if its moving downwards
					dropIndex = parentRow.SortOrder + bundleSize;
				}
				//If the dropped Item is also a packageHeader, move all of its children with it.
				if (params.row.IsPackageHeader) {
					const offsetRatio = 1 / (params.row.childRows.length + 1);
					//Account for the packageheader at the top of the bundle we are dragging in 
					if (offset < 0) {
						dropIndex += -1;
						offset = 0;
					}
					//Now move all bundleItems below the package header
					params.row.childRows.forEach((cRow, i) => {
						orderedRows.find((row) => row.idQuoteItems === cRow).SortOrder = (dropIndex + ( (i + 1) * offsetRatio) + offset);
					});
				}
				else if (params.row.IsPackageItem) {					
					//do not allow package items to leave the bounds of the bundle
					const parentOfDraggedItem = orderedRows.find((row)=>row.childRows?.includes(params.row.idQuoteItems));
					const bundleSizeOfDroppedParent = parentOfDraggedItem.childRows.length;
					dropIndex = Math.min(Math.max(dropIndex, parentOfDraggedItem.SortOrder + 1), parentOfDraggedItem.SortOrder + bundleSizeOfDroppedParent);
				}

			} else if (params.row.IsPackageHeader) {
				if (offset > 0 && orderedRows[params.targetIndex].IsPackageHeader) {
					const droppedOnBundleSize = orderedRows[params.targetIndex].childRows.length;
					dropIndex += droppedOnBundleSize;
				}

				const bundleSize = params.row.childRows.length;
				//If a package header is moved up, move all items its now above down by the bundle size
				for (let i = params.targetIndex; i <= params.oldIndex + 1; i++) {
					if (orderedRows.length <= i || i < 0) {
						continue;
					}
					orderedRows[i].SortOrder = orderedRows[i].SortOrder + bundleSize;
				}
				const offsetRatio = 1 / (params.row.childRows.length + 1);
				//Now move all bundleItems below the package header
				params.row.childRows.forEach((cRow, i) => {
					orderedRows.find((row) => row.idQuoteItems === cRow).SortOrder = (dropIndex + ( (i + 1) * offsetRatio) + offset);
				});

			}  else if (params.row.IsPackageItem) {
				//do not allow package items to leave the bounds of the bundle
				const parentRow = params.row.parentRow;
				const bundleSize = parentRow.childRows.length;
				dropIndex = Math.min(Math.max(dropIndex, parentRow.SortOrder + 1), parentRow.SortOrder + bundleSize);				
			}  else if (orderedRows[params.targetIndex].IsPackageHeader && offset > 0) {
				//If a drop occured below a package header
				const bundleSize = orderedRows[params.targetIndex].childRows.length;
				dropIndex += bundleSize;
			}

			orderedRows.find((row) => row.idQuoteItems === params.row.idQuoteItems).SortOrder = dropIndex + offset;
			orderedRows.sort((a, b) => a.SortOrder - b.SortOrder)
			orderedRows.forEach((row, index) => {
				row.SortOrder = index;
			});

			const rowsToUpdate = [];
			for (let i = 0; i < orderedRows.length; i++) {
				if (orderedRows[i].SortOrder !== gridRows.find((row=> row.idQuoteItems === orderedRows[i].idQuoteItems)).SortOrder) {
					rowsToUpdate.push(orderedRows[i]);
				}
			}
		
			//The row being dragged is dropeed in the same row it was dragged from
			if (rowsToUpdate.length === 0) {
				//For a rerender with the items in the pre-dropped order
				updateQuoteItems((items)=>[...items]);
				return [];
			}

			return rowsToUpdate.map((row, i) => {					
					return {
						fields: {
							SortOrder: row.SortOrder
						},
						queries: [
							{
								table: "QuoteItems",
								where: [
									{
										field: "IdQuoteItems",
										operator: "Equals",
										value: row.idQuoteItems
									}
								]
							}
						]
					};
				})
			},
		[gridRows, orderedRows]
	);

	const handleRowOrderChange = useCallback(
		(params) => {
			const updates = getFixRowOrderUpdates(params);
			if (updates.length === 0) {
				return;
			}
			setLoading(true);
			const updateApi = quosal.api.data.update(updates, quote.IdQuoteMain);
			updateApi.setFlag("fixRowOrder");
			updateApi.finished = function (msg) {
				quosal.sell.quote.updateFromApiResponse(msg);
				setLoading(false);
			};
			updateApi.call();
		},
		[getFixRowOrderUpdates, setLoading, tabItem?.IdQuoteTabs]
	);

	const handleCellClick = useCallback(
		(param, event) => {
			event.stopPropagation();
			const headers = getHeaders(tabItem?.IdQuoteTabs);
			const header = headers.filter((h) => h.FieldName === param.field);
			if (header.length) {
				const dataType = headers.filter((h) => h.FieldName === param.field)[0].DataType;
				if (dataType === "Boolean") {
					saveEditedField(param.row, param.field);
				}
			}
		},
		[saveEditedField, tabItem?.IdQuoteTabs]
	);

	const dataGridColumnHeaderWrapSx = {
		"& .MuiDataGrid-columnHeaderTitle": {
			whiteSpace: "normal",
			lineHeight: 1.2,
			overflow: "visible",
			textOverflow: "clip",
		},
		"& .MuiDataGrid-columnHeaderTitleContainer": {
			whiteSpace: "normal"
		},
		// Menu icon is positioned from the column header cell (not a padded inner box) so it stays inside resizable columns
		"& .MuiDataGrid-columnHeader": {
			position: "relative"
		},
		// Space for label/sort grows with column width; avoids a fixed px inset that breaks narrow columns
		"& .MuiDataGrid-columnHeaderDraggableContainer": {
			paddingRight: "clamp(2px, 12%, 26px)"
		},
		"& .MuiDataGrid-menuIcon": {
			position: "absolute",
			top: 0,
			bottom: 0,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			zIndex: 1,
			// Inset by separator width when defined on the grid root so the icon sits in the header, not on the resize line
			right: "var(--DataGrid-columnSeparatorSize, 0px)",
			// Frosted chip behind the ⋮ so it stays readable when header text wraps over a narrow column
			"& .MuiIconButton-root": {
				backgroundColor: darkMode ? "rgba(45, 45, 45, 0.92)" : "rgba(255, 255, 255, 0.92)",
				opacity: 1,
				"&:hover": {
					backgroundColor: darkMode ? "rgba(58,58,58,0.96)" : "rgba(255, 255, 255, 1)"
				}
			}
		}
	};

	const gridStyle =
		gridRows.length === 0
			? {
				...dataGridColumnHeaderWrapSx,
				"& .MuiDataGrid-columnHeaderTitleContainerContent input": {
					accentColor: "#000",
					pointerEvents: "none",
					opacity: 0.3
				},
				"& .MuiDataGrid-footerContainer": {
					marginBottom: "-34px",
					borderTop: "0",
				},
				"& .MuiDataGrid-cell--editable" : {
				cursor: "pointer"
				},
				"& .MuiDataGrid-overlayWrapper": {
					height: "50px"
				}
			}
			: {
				...dataGridColumnHeaderWrapSx,
				"& .MuiDataGrid-row": {
					bgcolor: darkMode ? "#1a1a1a" : "#fafafa"
				},
				".package": {
					bgcolor: darkMode ? "#444" : "#e0e0e0",
					fontWeight: "bolder",
					fontSize: "16px",
					"& .MuiDataGrid-cell": {
						fontWeight: "bolder",
						fontSize: "16px"
					},
					"& .MuiDataGrid-cell input": {
						fontWeight: "bolder",
						fontSize: "16px"
					}
				},
				".packageItem": {
					bgcolor: darkMode ? "#333" : "#eaeaea"
				},
				".subHeader": {
					bgcolor: darkMode ? "#333" : "#AFA"
				},
				".subFooter": {
					bgcolor: darkMode ? "#333" : "#8C8"
				},
				"& .MuiDataGrid-rowReorderCell": {
					color: "#2E3F80"
				},
				"& .MuiDataGrid-cell--editing:focus-within": {
					outline: "solid #2E3F80 1px !important"
				},
				"& .MuiDataGrid-footerContainer": {
					display: "none"
				},
				"& .MuiDataGrid-cell--editable" : {
					cursor: "pointer"
				},
				"& .MuiDataGrid-cell": {
					display: "flex",
					alignItems: "center",
				}
			};

	const addRecordStyle = {
		marginTop: "-4px",
		padding: "0 0 8px 20px",
		fontSize: "1rem",
		textTransform: "uppercase",
		cursor: "pointer",
		fontWeight: 500,
		...(darkMode ? { color: "var(--dark-mode-blue-color)" } : { color: "#2E3F80" })
	};

	const handleToDeleteRows = () => {
		const rowsToDelete = gridRows.filter((row) =>
			selectedRows.some((selRow) => selRow === row.idQuoteItems && !filteredRows.includes(row.idQuoteItems))
		);
		let rowsToDeleteWithPackageItems = rowsToDelete;
		let totalBundle = 0;
		let hasProtectedItem = false;
		rowsToDelete.forEach((row) => {
			if (row.isPackageHeader && (canModifyProtectedItem || !row.IsProtectedItem)) {
				totalBundle++;
				const packageItems = gridRows.filter((r) => r.ParentQuoteItem === row.idQuoteItems);
				rowsToDeleteWithPackageItems = [...new Set([...rowsToDeleteWithPackageItems, ...packageItems])];				
			}

			let removeItem = true;
			if (!canModifyProtectedItem && row.IsProtectedItem) {
				if (row.parentQuoteItem) {
					const parentItem = rowsToDelete.find((r) => r.idQuoteItems === row.parentQuoteItem);
					if (parentItem && !parentItem.IsProtectedItem) {
						removeItem = false;
					}
				}

				if (removeItem) {					
					hasProtectedItem = true;
					rowsToDeleteWithPackageItems = rowsToDeleteWithPackageItems.filter((r) => r.idQuoteItems !== row.idQuoteItems);
				}
			}
		});

		if (selectedRows.length) {
			onRemove(true, rowsToDeleteWithPackageItems, totalBundle > 0, totalBundle, hasProtectedItem, handleDeleteConfirmed);
		}
	};

	const groups = (() => {
		const data = quosal.util.clone(quosal.sell.product.sourceGroups);
		if (data && data.length) {
			data.sort((a, b) => {
				const textA = a.GroupName ? $.trim(a.GroupName.toUpperCase()) : "";
				const textB = b.GroupName ? $.trim(b.GroupName.toUpperCase()) : "";
				return textA < textB ? -1 : textA > textB ? 1 : 0;
			});
		}

		return data;
	})();

	for (let i = 0; i < groups.length; i++) {
		if (groups[i].IsPrimary && !defaultDropdownValue.length) {
			setDefaultDropdownValue(groups[i].IdProductSourceGroup);
		}
	}

	const openNewRecordDialog = (isShown) => {
		const currentTab = getCurrentTab();
		const tabId = tabItem?.IdQuoteTabs;
		const dataDialog = {
			currentTab,
			tabId,
			defaultDropdownValue
		};
		newRecordRef.current.showModal(isShown, dataDialog);
	};

	useEffect(() => {
		const insertBefore = selectedRows.length > 0 ? selectedRows[0] : undefined;
		contentGrid.handleInsertBefore(insertBefore);
		contentGrid.handleSelectedRows(selectedRows);
	}, [contentGrid, gridRows, selectedRows]);

	useEffect(() => {
		const gridProps = {
			selectedShowingRows: gridRows
				.map((el, i) => (selectedRows.includes(el.idQuoteItems) ? i : null))
				.filter((el) => el !== null),
			props: {
				rows: gridRows,
				headers: getHeaders(tabItem?.IdQuoteTabs),
				configuration: {
					gridName: "QuoteContent"
				}
			}
		};
		setGridData(gridProps);
	}, [selectedRows, gridRows, tabItem?.IdQuoteTabs]);

	return {
		openNewRecordDialog,
		handleToDeleteRows,
		setSelectedRows,
		processRowUpdate,
		handleRowOrderChange,
		handleCellClick,
		handleClose,
		handleDeleteConfirmed,
		setOpenMargins,
		getCurrentTab,
		handleModalClose,
		setEditableCell,
		setEditFieldPrevValue,
		isDisabledGridActions,
		calculateMargins,
		updateFieldCallback,
		handleInsertSectionGrouping,
		disableNewProductCreationStyle,
		gridStyle,
		addRecordStyle,
		isStandardUserAndProtectedTab,
		gridRows,
		gridColumns,
		selectedRows,
		openDialog,
		rowToDelete,
		openMargins,
		contentGrid,
		id,
		open,
		anchorEl,
		modalData,
		rowActions,
		expanded,
		openCustomizeGrid,
		isStandardUser,
		isStandardUserAndDisableNewProductCreation,
		isStandardUserPlus,
		isStandardUserAndLockItemEdit,
	};
};

export default useDataGridBoilerplateContainer;
